import { formatDate } from "@angular/common";
import { HttpErrorResponse } from "@angular/common/http";
import { Component, inject, input, OnDestroy, OnInit, output, TemplateRef } from "@angular/core";
import { MatLegacyDialog as MatDialog } from "@angular/material/legacy-dialog";
import { pushError } from "@app/_helpers/globalErrorHandler";
import { isWorkingHoursError } from "@app/_helpers/is-error-object";
import { hasPermissionByKey } from "@app/_helpers/permission";
import { coerceTimeFormat, createRxValue, fromRxValue } from "@app/_helpers/utils";
import { ClockService } from "@app/_services/clock.service";
import { AppService } from "@app/app.service";
import { RecordToolbarService } from "@app/shared/record-toolbar/record-toolbar.service";
import { environment } from "@env/environment";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { ComegoQuery, ComegoService, ComegoTime, ComegoTimeType, Logger, UserSettingsQuery } from "@timeghost/web-api";
import { addMinutes } from "date-fns";
import { isBefore, isSameDay, isSameMinute, roundToNearestMinutes, startOfMinute } from "date-fns/esm";
import produce from "immer";
import { combineLatest, distinctUntilChanged, map } from "rxjs";
import {
	ComeAndGoAbsenceDialogComponent,
	WorkingHoursAbsenceData,
} from "../come-and-go-absence-dialog/come-and-go-absence-dialog.component";
import {
	ComeAndGoCreateDialogComponent,
	WorkingHoursCreateData,
} from "../come-and-go-create-dialog/come-and-go-create-dialog.component";
import { COMEGO_ICON_MAP } from "../come-and-go-create-dialog/come-and-go-utils";
import {
	ComeAndGoUpdateDialogComponent,
	ComeGoUpdateDialogData,
} from "../come-and-go-update-dialog/come-and-go-update-dialog.component";

const log = new Logger("RecordComegoWidgetComponent");
/** Same window as come-go box action cooldown; keeps debounced UI in sync and limits spam. */
const ACTION_COOLDOWN_AFTER_CLICK_MS = 1000;
@UntilDestroy()
@Component({
	selector: "tg-record-comego-widget",
	templateUrl: "./record-comego-widget.component.html",
	styleUrls: ["./record-comego-widget.component.scss"],
})
export class RecordComegoWidgetComponent implements OnInit, OnDestroy {
	private readonly clock = inject(ClockService);
	readonly iconMap = COMEGO_ICON_MAP;
	readonly switchEnabled = input(null, { alias: "switch", transform: (v) => v === false || true });
	readonly onSwitch = output();
	readonly loading$ = createRxValue(false);
	private readonly actionCooldown = createRxValue(false);
	readonly actionCooldown$ = this.actionCooldown.value$;
	private cooldownClearHandle: ReturnType<typeof setTimeout> | null = null;
	readonly comegoRunning$ = fromRxValue(
		combineLatest([
			this.userSettingsQuery.select(),
			this.comegoQuery.selectAll({
				filterBy: (x) => !x.end,
			}),
		]).pipe(map(([user, times]) => times.find((x) => x.user?.id === user.id)))
	);
	readonly running$ = combineLatest([this.comegoRunning$.asObservable(), this.clock.every$(1000)]).pipe(
		untilDestroyed(this),
		distinctUntilChanged(([prevTime], [time]) => (!!time || (prevTime && !prevTime.end) ? false : true)),
		map(([time, now]) => (time ? { ...time, now } : time))
	);
	constructor(
		private appService: AppService,
		private comegoQuery: ComegoQuery,
		private comegoService: ComegoService,
		private userSettingsQuery: UserSettingsQuery,
		private recordService: RecordToolbarService,
		private dialog: MatDialog
	) {}
	readonly comegoEnabled$ = this.appService.comegoEnabled$;
	readonly canAdd$ = fromRxValue(
		this.userSettingsQuery.select().pipe(map((x) => hasPermissionByKey(x, "groupsCanComegoCreateTimes" as any))),
		false
	);
	readonly workspace$canCreateAbsences = this.userSettingsQuery
		.select()
		.pipe(map((x) => hasPermissionByKey(x, "groupsCanCreateAbsences" as any)));
	readonly hasOverlappingEntry$ = combineLatest([
		this.clock.every$(1000),
		this.comegoQuery.selectAll({
			filterBy: (x) =>
				x.user?.id === this.userSettingsQuery.getValue().id && isSameDay(new Date(x.start), new Date()),
		}),
	]).pipe(
		map(([now, comegos]) => {
			return comegos.some((entry) => {
				if (entry.type !== "work" && entry.type !== "pause") return false;
				const start = new Date(entry.start);
				const end = entry.end ? new Date(entry.end) : null;
				if (!end) return now >= start;
				return now >= start && now < end;
			});
		})
	);
	ngOnInit(): void {}
	ngOnDestroy(): void {
		if (this.cooldownClearHandle !== null) clearTimeout(this.cooldownClearHandle);
	}
	private armActionCooldown() {
		if (this.cooldownClearHandle !== null) clearTimeout(this.cooldownClearHandle);
		this.actionCooldown.update(true);
		this.cooldownClearHandle = setTimeout(() => {
			this.actionCooldown.update(false);
			this.cooldownClearHandle = null;
		}, ACTION_COOLDOWN_AFTER_CLICK_MS);
	}
	//disable recording feature to prevent overlap
	readonly forXMinutes = createRxValue("");
	readonly disabledUntil = this.recordService.disabledUntil;
	readonly disableRecording = combineLatest([this.disabledUntil.asObservable()]).pipe(
		map(([until]) => {
			if (!until) return false;
			const now = new Date();
			const untilTime = coerceTimeFormat(until);
			if (untilTime <= now) this.disabledUntil.update(null);
			return untilTime > now;
		})
	);
	readonly isSameMinute$ = combineLatest([this.comegoRunning$.asObservable(), this.clock.now$]).pipe(
		map(([time, now]) =>
			time ? isSameMinute(new Date(time.start), roundToNearestMinutes(new Date(), { nearestTo: 1 })) : false
		)
	);
	async openCreateDialog(state?: "default" | "absence") {
		if (state === "absence") {
			return await this.dialog
				.open(ComeAndGoAbsenceDialogComponent, {
					data: {} as WorkingHoursAbsenceData,
				})
				.afterClosed()
				.toPromise();
		}
		return await this.dialog
			.open(ComeAndGoCreateDialogComponent, {
				data: {} as WorkingHoursCreateData,
			})
			.afterClosed()
			.toPromise();
	}
	openUpdateDialog(time: ComegoTime) {
		return this.dialog.open(ComeAndGoUpdateDialogComponent, {
			data: <ComeGoUpdateDialogData>{
				entity: time,
				isPastTime: false,
			},
		});
	}

	async startRecord(type?: ComegoTimeType) {
		const now = roundToNearestMinutes(new Date(), { nearestTo: 1 });
		const todayEntries = this.comegoQuery.getAll({
			filterBy: (x) => x.user?.id === this.userSettingsQuery.getValue().id && isSameDay(new Date(x.start), now),
		});
		const overlapping = todayEntries.some((entry) => {
			if (entry.type !== "work" && entry.type !== "pause") return false;
			const start = new Date(entry.start);
			const end = entry.end ? new Date(entry.end) : null;
			if (!end) return now >= start;
			return now >= start && now < end;
		});
		if (overlapping) {
			this.appService.notifyError("errors.times.comego.already_recording");
			return null;
		}
		this.loading$.next(true);
		const cuser = this.userSettingsQuery.getValue();
		const user = cuser.workspace.users.find((x) => x.id === cuser.id);
		const start = now;
		const previousEntry = todayEntries
			.sort((a, b) => new Date(b.start).getTime() - new Date(a.start).getTime())
			.find((x) => new Date(x.end) <= now);
		if (previousEntry?.type === "work" && type !== "pause") {
			if (!isSameMinute(new Date(previousEntry.end), now)) {
				const pauseData = {
					name: null,
					start: new Date(previousEntry.end),
					end: now,
					type: "pause",
					user,
				} as any;
				await this.comegoService.add(pauseData);
				this.appService.notifySuccess("comego.gapFilled");
			}
		} else if (previousEntry?.type === "pause" && type !== "pause" && previousEntry.end) {
			if (!isSameMinute(new Date(previousEntry.end), now)) {
				await this.comegoService.update({
					...previousEntry,
					end: now,
					user,
				} as any);
				this.appService.notifySuccess("comego.pauseAdjusted");
			}
		}
		const times = await this.comegoService
			.add({
				name: null,
				start,
				end: null,
				type: type ?? "work",
				user,
			} as any)
			.then(this.recordService.handleWorkingHourSuccess.bind(this.recordService))
			.catch(async (err: HttpErrorResponse) => {
				pushError(err);
				if (err.error.message == "errors.times.comego.breakRuleNotice-until") {
					const now = startOfMinute(new Date());
					const date = addMinutes(now, err.error.args.value);
					this.disabledUntil.update(formatDate(date, "HH:mm", "en-US"));
					// disabledUNtil.subscribe() => cunctruct date  if date == now  => enabled
				}
				if (isWorkingHoursError(err.error))
					await this.recordService.handleWorkingHourError.bind(this.recordService)(err);
				return null;
			});
		log.debug(times);
		this.resetLoading();
		return times?.[0];
	}
	async stopRecord(edit?: boolean) {
		const result = await this.runStopWithRuleCheck(edit, true);
		return result?.entry ?? null;
	}

	private async runStopWithRuleCheck(
		edit?: boolean,
		alsoStopLinkedProject = false
	): Promise<{ entry: ComegoTime | null; appliedFix: boolean } | null> {
		this.loading$.next(true);
		const time = this.comegoRunning$.value;
		if (!time) {
			this.resetLoading();
			return null;
		}
		const pairedWhId = time.id;
		const stopResult = await this.recordService.stopComegoWithRuleCheck(time, () =>
			this.recordService.performNormalComegoStop(time)
		);
		if (stopResult.entry && !stopResult.appliedFix) this.recordService.notifySuccess("success.created");
		if (pairedWhId && stopResult.entry != null) {
			await this.recordService.afterPairedWorkingHoursStopped(pairedWhId, alsoStopLinkedProject);
		}
		let entry = stopResult.entry;
		if (edit && entry) {
			const pastUpdatedTime = await this.openUpdateDialog(entry).afterClosed().toPromise();
			if (pastUpdatedTime) entry = pastUpdatedTime;
		}
		this.resetLoading();
		return { entry, appliedFix: stopResult.appliedFix };
	}

	private async toggleRecording(targetType: ComegoTimeType) {
		this.armActionCooldown();
		const stopResult = await this.runStopWithRuleCheck(undefined, false);
		if (!stopResult?.entry) return;
		const openAfterFix = this.comegoRunning$.value;
		if (stopResult.appliedFix && openAfterFix) {
			// Dialog already persisted the suggested outcome (e.g. active pause or resume work).
			return;
		}
		await this.startRecord(targetType);
	}

	async pause(id: string) {
		await this.toggleRecording("pause");
	}
	async resume(id: string) {
		await this.toggleRecording("work");
	}
	async delete(id: string, template?: TemplateRef<any>) {
		if (template) {
			const allowDelete = await this.dialog
				.open(template, {
					data: {
						entity: this.comegoRunning$.value,
					},
				})
				.afterClosed()
				.toPromise()
				.then((x) => x === true)
				.catch(() => false);
			if (!allowDelete) return;
		}
		this.loading$.next(true);
		return await this.comegoService
			.delete({ id })
			.then(async () => {
				await this.recordService.clearDualStartedProjectHoursRecording(id);
			})
			.finally(() => {
				this.resetLoading();
				this.recordService.notifySuccess("success.recordingDiscarded");
			});
	}
	resetLoading() {
		this.loading$.next(false);
	}
}
